import { drag, select } from "d3";
import { RefObject, useEffect, useMemo, useState } from "react";

import { usePlayContext } from "../../../pages/Play/play-context";
import { Action, DragGroupElement, Marker, MarkerType, Point, StencilType } from "../../../types";
import { MovementTimeScale, createMovementScale, isAnimateableStencil } from "../../../utils";
import { usePlayingSurfaceContext } from "../../playing-surface/playing-surface-context";
import { clearAssociationCircleHighlights, getComparisonStencils, highlightAssociatedStencils } from "../utils";

export type UseMoveStencilProps = {
  stencilWrapperRef: RefObject<SVGGElement>;
  stencil: Action | Marker;
};

export const useMoveStencil = ({ stencilWrapperRef, stencil }: UseMoveStencilProps) => {
  const { id } = stencil;

  const isPlayer = stencil.stencilType === StencilType.MARKER && stencil.markerType === MarkerType.PLAYER;
  const isBall = stencil.stencilType === StencilType.MARKER && stencil.markerType === MarkerType.BALL;

  const { selectItemApi, frameStencilsApi, playStateApi, drawToolbarApi, animationTimerApi } = usePlayContext();
  const { selectItem } = selectItemApi;
  const { updateStencilPosition } = frameStencilsApi;
  const { activeFrameStencils } = playStateApi;
  const { animationView, freeDraw } = drawToolbarApi;
  const { effectiveTime } = animationTimerApi;

  const isFreeDrawActive = freeDraw.isActive;

  const { isEditable } = usePlayingSurfaceContext();

  const scale: MovementTimeScale = useMemo(() => {
    if (isAnimateableStencil(stencil) && animationView.isActive && isEditable) {
      return createMovementScale({ marker: stencil, stencils: activeFrameStencils });
    } else return () => stencil.pos;
  }, [stencil, isEditable, animationView, activeFrameStencils]);

  const pos: Point = useMemo(() => scale(effectiveTime), [effectiveTime, scale]);

  // position of stencil wrapper only controlled by external props, used to calculate x and y offsets
  const [groupStaticPos, setGroupStaticPos] = useState<Point>(pos);
  // position of stencil wrapper that is controlled both by external props and internal drag events
  const [groupTempPos, setGroupTempPos] = useState<Point>(pos);

  // update positions when external pos changes
  useEffect(() => {
    setGroupStaticPos(pos);
    setGroupTempPos(pos);
  }, [pos]);

  const isMoveable = isEditable && !((isPlayer || isBall) && isFreeDrawActive);

  /** ======== Move Drag Control ======== */
  useEffect(() => {
    const g = stencilWrapperRef.current;

    if (g && isMoveable) {
      const comparisonStencils = getComparisonStencils({ frameStencils: activeFrameStencils, stencil });

      // select group element
      select<SVGGElement, undefined>(g).call(
        drag<SVGGElement, undefined>()
          // on drag start, select item by id
          .on("start", function () {
            selectItem(id);
          })
          // on drag, calculate new pos and set the temp position
          .on("drag", function (event: DragGroupElement) {
            const { x, y, subject } = event;
            const { x: subjectX, y: subjectY } = subject;

            const newX = x - (subjectX - groupStaticPos.x);
            const newY = y - (subjectY - groupStaticPos.y);

            highlightAssociatedStencils({ ...comparisonStencils, newX, newY });
            setGroupTempPos({ x: newX, y: newY });
          })
          // on drag end, calculate delta position and update position in data state
          .on("end", function (event: DragGroupElement) {
            const { x, y, subject } = event;
            const { x: subjectX, y: subjectY } = subject;

            const deltaX = x - subjectX;
            const deltaY = y - subjectY;

            clearAssociationCircleHighlights();

            if (deltaX !== 0 || deltaY !== 0) updateStencilPosition({ id, deltaX, deltaY });
          })
      );
    } else if (g && !isMoveable) {
      select<SVGGElement, undefined>(g).on("mousedown.drag", null);
    }
  }, [
    stencilWrapperRef,
    id,
    selectItem,
    groupStaticPos,
    updateStencilPosition,
    stencil,
    activeFrameStencils,
    isMoveable,
  ]);

  return { groupTempPos };
};
